import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import axios from 'axios';
import {
    TvIcon,
    VideoCameraIcon,
    DocumentTextIcon,
    CurrencyEuroIcon,
    BellIcon,
    UserCircleIcon,
    ArrowRightIcon,
    PlayCircleIcon,
    ArrowDownTrayIcon,
    PauseCircleIcon,
    SignalSlashIcon,
    ClockIcon,
    RectangleGroupIcon,
} from "@heroicons/react/24/outline";

const apiBase = () => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;
const FLEET_POLL_MS = 10000;

// ------------------------------------------------------------------ Dashboard fleet (KPIs live)

const KPI_DEFS = [
    { key: "playing", label: "En diffusion", icon: PlayCircleIcon, wrap: "bg-emerald-500/10 text-emerald-600", bar: "bg-emerald-500" },
    { key: "downloading", label: "Synchronisation", icon: ArrowDownTrayIcon, wrap: "bg-amber-500/10 text-amber-600", bar: "bg-amber-500" },
    { key: "idle", label: "À l'arrêt", icon: PauseCircleIcon, wrap: "bg-slate-500/10 text-slate-600", bar: "bg-slate-400" },
    { key: "offline", label: "Hors ligne", icon: SignalSlashIcon, wrap: "bg-red-500/10 text-red-600", bar: "bg-red-500" },
];

const PLATFORM_CHIP = {
    ANDROID: "bg-green-100 text-green-700",
    WEBOS: "bg-pink-100 text-pink-700",
    TIZEN: "bg-blue-100 text-blue-700",
    ELECTRON: "bg-cyan-100 text-cyan-700",
    WEBAPP: "bg-indigo-100 text-indigo-700",
};

function fmtClock(ms) {
    const t = Math.max(0, Math.floor((ms || 0) / 1000));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}

function relTime(value) {
    if (!value) return "—";
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return "—";
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return "à l'instant";
    const m = Math.floor(s / 60);
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.floor(h / 24)} j`;
}

function deployBadge(status) {
    if (status === "Published") return "bg-emerald-100 text-emerald-700";
    if (status === "Published (Cached)") return "bg-teal-100 text-teal-700";
    if (status === "In Progress") return "bg-blue-100 text-blue-700";
    if (status === "Failed") return "bg-red-100 text-red-700";
    return "bg-slate-100 text-slate-600";
}

function KpiRow({ counts, loading, apiDown }) {
    const total = counts.total || 0;
    return (
        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {KPI_DEFS.map((k) => {
                const Icon = k.icon;
                const value = apiDown ? "—" : (counts[k.key] ?? 0);
                const pct = !apiDown && total > 0 ? Math.round(((counts[k.key] || 0) / total) * 100) : 0;
                return (
                    <Link
                        key={k.key}
                        to="/nowplaying"
                        className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                        <div className="flex items-center justify-between">
                            <div className={`grid h-10 w-10 place-items-center rounded-xl ${k.wrap}`}>
                                <Icon className="h-5 w-5" />
                            </div>
                            <span className="text-3xl font-bold tabular-nums text-slate-800">
                                {loading ? "…" : value}
                            </span>
                        </div>
                        <p className="mt-3 text-sm font-medium text-slate-500">{k.label}</p>
                        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                            <div className={`h-full rounded-full ${k.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                    </Link>
                );
            })}
        </section>
    );
}

function LivePanel({ devices, loading, apiDown }) {
    const playing = (devices || []).filter((d) => d.state === "playing").slice(0, 5);
    return (
        <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    </span>
                    En diffusion maintenant
                </h3>
                <Link to="/nowplaying" className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800">
                    Tout voir <ArrowRightIcon className="h-3.5 w-3.5" />
                </Link>
            </div>

            {apiDown ? (
                <p className="py-8 text-center text-sm text-slate-400">API injoignable — statut indisponible.</p>
            ) : loading ? (
                <p className="py-8 text-center text-sm text-slate-400">Chargement…</p>
            ) : playing.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Aucun écran ne diffuse actuellement.</p>
            ) : (
                <ul className="space-y-3">
                    {playing.map((d) => {
                        const hasDur = d.durationMs > 0;
                        const pct = hasDur ? Math.min(100, (d.positionMs / d.durationMs) * 100) : 0;
                        return (
                            <li key={d.pinCode} className="rounded-xl bg-slate-50 px-3 py-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-sm font-medium text-slate-700">
                                        {d.clientName || `PIN ${d.pinCode}`}
                                    </span>
                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${PLATFORM_CHIP[d.platform] || "bg-slate-100 text-slate-600"}`}>
                                        {d.platform || "—"}
                                    </span>
                                </div>
                                <div className="mt-1 truncate text-xs text-slate-400">{d.videoName || "—"}</div>
                                <div className="mt-2 flex items-center gap-2">
                                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200">
                                        <div className="h-full rounded-full bg-emerald-500" style={{ width: hasDur ? `${pct}%` : "33%" }} />
                                    </div>
                                    <span className="font-mono text-[10px] tabular-nums text-slate-400">
                                        {fmtClock(d.positionMs)}{hasDur ? ` / ${fmtClock(d.durationMs)}` : ""}
                                    </span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function DeploymentsPanel() {
    const [sessions, setSessions] = useState(null); // null = loading, [] = vide, false = erreur

    useEffect(() => {
        let alive = true;
        axios.get(`${apiBase()}/download_history/sessions?page=1&pageSize=5`, { timeout: 10000 })
            .then((res) => { if (alive) setSessions(res.data?.sessions || []); })
            .catch(() => { if (alive) setSessions(false); });
        return () => { alive = false; };
    }, []);

    return (
        <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                    <ClockIcon className="h-5 w-5 text-slate-400" />
                    Derniers déploiements
                </h3>
                <Link to="/deployment-history" className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800">
                    Historique <ArrowRightIcon className="h-3.5 w-3.5" />
                </Link>
            </div>

            {sessions === null ? (
                <p className="py-8 text-center text-sm text-slate-400">Chargement…</p>
            ) : sessions === false ? (
                <p className="py-8 text-center text-sm text-slate-400">Historique indisponible.</p>
            ) : sessions.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Aucun déploiement récent.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {sessions.map((s) => (
                        <li key={`${s.sessionId}-${s.pinCode}`} className="flex items-center justify-between gap-3 py-2.5">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-slate-700">
                                    {s.clientName || `PIN ${s.pinCode}`}
                                </p>
                                <p className="text-xs text-slate-400">
                                    {(s.filesSuccess || 0) + (s.filesCached || 0)}/{s.filesTotal || 0} fichiers · {relTime(s.dateModified)}
                                </p>
                            </div>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${deployBadge(s.publishStatus)}`}>
                                {s.publishStatus}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ------------------------------------------------------------------ Accès modules (raccourcis)

const MODULES = [
    { title: "Écrans", subtitle: "Parc & approbations", icon: TvIcon, iconWrap: "bg-indigo-500/10 text-indigo-600", accent: "from-indigo-500 to-blue-500",
      links: [
        { label: "Écrans approuvés", to: "/listtizenbo" },
        { label: "En attente d'approbation", to: "/listnonapprouve" },
        { label: "Captures d'écran", to: "/imagemanager" },
      ] },
    { title: "Diffusion", subtitle: "Programmes & planning", icon: RectangleGroupIcon, iconWrap: "bg-blue-500/10 text-blue-600", accent: "from-blue-500 to-cyan-500",
      links: [
        { label: "Programmes (cartes)", to: "/listclientprogramme" },
        { label: "Programmes (liste)", to: "/listprogramme" },
        { label: "Murs d'écrans", to: "/listvideowall" },
      ] },
    { title: "Contenu", subtitle: "Médias & fichiers", icon: VideoCameraIcon, iconWrap: "bg-sky-500/10 text-sky-600", accent: "from-sky-500 to-cyan-500",
      links: [
        { label: "Uploader un média", to: "/uploadfile" },
        { label: "Gestion des fichiers", to: "/filemanager" },
      ] },
    { title: "Price-Eat", subtitle: "Prix & articles", icon: CurrencyEuroIcon, iconWrap: "bg-emerald-500/10 text-emerald-600", accent: "from-emerald-500 to-teal-500",
      links: [
        { label: "Utilisateurs", to: "/listusers" },
        { label: "Articles & prix", to: "/listidpdv" },
      ] },
    { title: "Call-Eat", subtitle: "Bornes de commande", icon: BellIcon, iconWrap: "bg-amber-500/10 text-amber-600", accent: "from-amber-500 to-orange-500",
      links: [{ label: "Clients Call-Eat", to: "/listcalleat" }] },
    { title: "Réseau & Annuaire", subtitle: "Parc & connexions", icon: UserCircleIcon, iconWrap: "bg-violet-500/10 text-violet-600", accent: "from-violet-500 to-fuchsia-500",
      links: [
        { label: "Client Samsung / LG", to: "/annuaireclient" },
        { label: "Serveur Samsung / LG", to: "/annuaireserver" },
        { label: "Wireguard", to: "/wireguard" },
        { label: "Annuaire Alta", to: "/annuairealtaclient" },
      ] },
    { title: "Ressources", subtitle: "Guides & liens", icon: DocumentTextIcon, iconWrap: "bg-slate-500/10 text-slate-600", accent: "from-slate-500 to-slate-700",
      links: [
        { label: "Documentation", to: "/documentation" },
        { label: "Liens de téléchargement", to: "/listlink" },
      ] },
];

function Home() {
    const { keycloak, initialized } = useKeycloak();
    const [postLoginActionCompleted, setPostLoginActionCompleted] = useState(false);
    const [fleet, setFleet] = useState({ counts: {}, devices: [] });
    const [fleetLoading, setFleetLoading] = useState(true);
    const [apiDown, setApiDown] = useState(false);

    const firstName = keycloak?.tokenParsed?.given_name || "";
    const today = new Date().toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

    // Actions post-connexion (inchangé) : récupération du token de service PriceEat.
    useEffect(() => {
        if (initialized && keycloak.authenticated && !postLoginActionCompleted) {
            const performPostLoginActions = async () => {
                try {
                    const data = { username: "admin", password: "admin" };
                    const result = await axios.post(`${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}/api/auth/signin`, data);

                    if (result.data.roles && result.data.roles[0] === "ROLE_ADMIN") {
                        localStorage.setItem('id_pdv_local', result.data.id_pdv);
                        localStorage.setItem('token', result.data.accessToken);
                    }
                    localStorage.setItem('key', keycloak.token);
                    setPostLoginActionCompleted(true);
                } catch (error) {
                    console.error("Erreur lors de l'exécution des actions post-connexion:", error);
                }
            };
            performPostLoginActions();
        }
    }, [initialized, keycloak.authenticated, keycloak.token, postLoginActionCompleted]);

    // Poll fleet /now_playing pour les KPIs et le panneau "En diffusion maintenant".
    useEffect(() => {
        let alive = true;
        const poll = async () => {
            try {
                const res = await axios.get(`${apiBase()}/now_playing`, { timeout: 8000 });
                if (!alive) return;
                setFleet(res.data || { counts: {}, devices: [] });
                setApiDown(false);
            } catch {
                if (alive) setApiDown(true);
            } finally {
                if (alive) setFleetLoading(false);
            }
        };
        poll();
        const id = setInterval(poll, FLEET_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    return (
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
            {/* Hero compact */}
            <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-6 shadow-xl sm:p-8">
                <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
                <div className="relative flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-sm font-medium capitalize text-indigo-300">{today}</p>
                        <h1 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                            Bonjour{firstName ? `, ${firstName}` : ""} 👋
                        </h1>
                        <p className="mt-2 max-w-xl text-sm text-slate-300">
                            Vue d'ensemble du parc — écrans, diffusion et contenus en un coup d'œil.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to="/uploadfile" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:bg-indigo-400">
                            <VideoCameraIcon className="h-4 w-4" /> Uploader un média
                        </Link>
                        <Link to="/listclientprogramme" className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 backdrop-blur transition hover:bg-white/10 hover:text-white">
                            Programmer <ArrowRightIcon className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* KPIs live du parc */}
            <KpiRow counts={fleet.counts || {}} loading={fleetLoading} apiDown={apiDown} />

            {/* Diffusion en cours + derniers déploiements */}
            <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <LivePanel devices={fleet.devices} loading={fleetLoading} apiDown={apiDown} />
                <DeploymentsPanel />
            </section>

            {/* Accès rapides aux modules */}
            <section>
                <h2 className="mb-4 text-lg font-semibold text-slate-800">Modules</h2>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {MODULES.map((m) => {
                        const Icon = m.icon;
                        return (
                            <div
                                key={m.title}
                                className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl"
                            >
                                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.accent}`} />
                                <div className="mb-4 flex items-center gap-3">
                                    <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${m.iconWrap}`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-800">{m.title}</h3>
                                        <p className="text-xs text-slate-400">{m.subtitle}</p>
                                    </div>
                                </div>
                                <div className="space-y-0.5">
                                    {m.links.map((l) => (
                                        <Link
                                            key={l.to}
                                            to={l.to}
                                            className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                                        >
                                            <span className="truncate">{l.label}</span>
                                            <ArrowRightIcon className="h-4 w-4 shrink-0 -translate-x-1 text-slate-400 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

export default Home;
